import BaseApi from './BaseApi';
import { logError } from '../utils/errorLogger';

import moment from 'moment'
class BalanceValidationApi extends BaseApi {
  constructor() {
    super('hotel-reservation');
  }

  /**
   * Get partner balance summary including plafond and deposit information
   * @param {number} partnerId - The partner ID
   * @param {string} currentDate - Current date (optional)
   * @returns {Promise} Partner balance summary
   */
  async getPartnerBalance(partnerId, currentDate = null) {
    try {
      const params = currentDate ? { currentDate } : {};
      const response = await this.getApiInstance(true).get(
        `/${this.endpoint}/partner/${partnerId}/balance`,
        { params }
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  /**
   * Validate payment methods against partner balance
   * @param {Object} data - Validation data
   * @param {number} data.partnerId - The partner ID
   * @param {Object} data.methodePaiement - Payment methods object
   * @param {number} data.totalAmount - Total reservation amount
   * @param {string} data.currentDate - Current date (optional)
   * @returns {Promise} Validation result
   */
  async validatePaymentBalance(data) {
    try {
      const response = await this.getApiInstance(true).post(
        `/${this.endpoint}/validate-payment-balance`,
        data
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  /**
   * Get partner payment eligibility for reservation amount/date
   * @param {number} partnerId - Partner id
   * @param {Object} data - Eligibility payload
   * @param {string} data.checkInDate - Reservation check-in date
   * @param {number} data.totalAmount - Amount to cover
   * @returns {Promise<Object>} Eligibility response
   */
  async getPartnerPaymentEligibility(partnerId, data) {
    try {
      const response = await this.getApiInstance(true).post(
        `/${this.endpoint}/partner/${partnerId}/payment-eligibility`,
        data
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  /**
   * Get all payment modes with champs_saisie (physique-like)
   * @returns {Promise<Array>} modes
   */
  async getAllPaymentModesWithChamps() {
    try {
      const response = await this.getApiInstance(true).get(`/paiements/with-champs`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  /**
   * Format balance information for display
   * @param {Object} balanceInfo - Balance information from API
   * @returns {Object} Formatted balance display
   */
  formatBalanceDisplay(balanceInfo) {
    const result = {
      plafond: null,
      deposit: null,
      warnings: [],
      errors: []
    };

    if (balanceInfo.plafond) {
      const plafond = balanceInfo.plafond;
      result.plafond = {
        hasPlafond: plafond.hasPlafond,
        totalPlafond: plafond.totalPlafond || 0,
        usedAmount: plafond.usedAmount || 0,
        availableAmount: plafond.availableAmount || 0,
        plafondType: plafond.plafondType,
        isValid: plafond.isValid,
        formatted: {
          total: `${(plafond.totalPlafond || 0).toFixed(2)} TND`,
          used: `${(plafond.usedAmount || 0).toFixed(2)} TND`,
          available: `${(plafond.availableAmount || 0).toFixed(2)} TND`,
          percentage: plafond.totalPlafond > 0 
            ? Math.round((plafond.usedAmount / plafond.totalPlafond) * 100)
            : 0
        }
      };

      if (!plafond.hasPlafond) {
        result.warnings.push('Aucun plafond configuré pour ce partenaire');
      } else if (plafond.availableAmount <= 0) {
        result.errors.push('Plafond épuisé');
      } else if (plafond.availableAmount < 100) {
        result.warnings.push('Plafond bientôt épuisé');
      }

      if (plafond.plafondType === 'avec_date' && plafond.plafondPeriod) {
        result.plafond.period = {
          from: plafond.plafondPeriod.dateFrom,
          to: plafond.plafondPeriod.dateTo,
          formatted: `${moment(plafond.plafondPeriod.dateFrom).format('DD/MM/YYYY')} - ${moment(plafond.plafondPeriod.dateTo).format('DD/MM/YYYY')}`
        };
      }
    }

    if (balanceInfo.deposit) {
      const deposit = balanceInfo.deposit;
      result.deposit = {
        hasDeposit: deposit.hasDeposit,
        totalDeposits: deposit.totalDeposits || 0,
        usedAmount: deposit.usedAmount || 0,
        availableAmount: deposit.availableAmount || 0,
        isValid: deposit.isValid,
        formatted: {
          total: `${(deposit.totalDeposits || 0).toFixed(2)} TND`,
          used: `${(deposit.usedAmount || 0).toFixed(2)} TND`,
          available: `${(deposit.availableAmount || 0).toFixed(2)} TND`,
          percentage: deposit.totalDeposits > 0 
            ? Math.round((deposit.usedAmount / deposit.totalDeposits) * 100)
            : 0
        }
      };

      if (!deposit.hasDeposit) {
        result.warnings.push('Aucun dépôt disponible pour ce partenaire');
      } else if (deposit.availableAmount <= 0) {
        result.errors.push('Dépôt épuisé');
      } else if (deposit.availableAmount < 100) {
        result.warnings.push('Dépôt bientôt épuisé');
      }
    }

    return result;
  }

  /**
   * Check if a payment method amount is valid against available balance
   * @param {Object} balanceInfo - Balance information
   * @param {string} paymentType - Payment type ('debiteur' or 'deposit')
   * @param {number} amount - Amount to check
   * @returns {Object} Validation result
   */
  checkPaymentAmount(balanceInfo, paymentType, amount) {
    const result = {
      isValid: true,
      message: '',
      severity: 'info' // 'success', 'warning', 'error'
    };

    if (paymentType === 'debiteur' && balanceInfo.plafond) {
      if (!balanceInfo.plafond.hasPlafond) {
        result.isValid = false;
        result.message = 'Aucun plafond configuré';
        result.severity = 'error';
      } else if (amount > balanceInfo.plafond.availableAmount) {
        result.isValid = false;
        result.message = `Plafond insuffisant (disponible: ${balanceInfo.plafond.formatted.available})`;
        result.severity = 'error';
      } else if (amount > balanceInfo.plafond.availableAmount * 0.8) {
        result.message = `Attention: utilisation importante du plafond (${Math.round((amount / balanceInfo.plafond.totalPlafond) * 100)}%)`;
        result.severity = 'warning';
      } else {
        result.message = `Plafond OK (reste: ${(balanceInfo.plafond.availableAmount - amount).toFixed(2)} TND)`;
        result.severity = 'success';
      }
    }

    if (paymentType === 'deposit' && balanceInfo.deposit) {
      if (!balanceInfo.deposit.hasDeposit) {
        result.isValid = false;
        result.message = 'Aucun dépôt disponible';
        result.severity = 'error';
      } else if (amount > balanceInfo.deposit.availableAmount) {
        result.isValid = false;
        result.message = `Dépôt insuffisant (disponible: ${balanceInfo.deposit.formatted.available})`;
        result.severity = 'error';
      } else if (amount > balanceInfo.deposit.availableAmount * 0.8) {
        result.message = `Attention: utilisation importante du dépôt (${Math.round((amount / balanceInfo.deposit.totalDeposits) * 100)}%)`;
        result.severity = 'warning';
      } else {
        result.message = `Dépôt OK (reste: ${(balanceInfo.deposit.availableAmount - amount).toFixed(2)} TND)`;
        result.severity = 'success';
      }
    }

    return result;
  }
}

export default new BalanceValidationApi(); 